import { useEffect } from 'react';
import { Turnstile as TurnstileWidget } from '@marsidev/react-turnstile';

interface TurnstileProps {
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
}

export const Turnstile = ({ onSuccess, onError, onExpire }: TurnstileProps) => {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  // If no site key, bypass CAPTCHA for development
  useEffect(() => {
    if (!siteKey) {
      console.warn('Turnstile site key not configured - CAPTCHA disabled for development');
      // Auto-call onSuccess with a development token so signup works without CAPTCHA
      // This should only happen in development - production must have the key configured
      onSuccess('dev-bypass-token');
    }
  }, [siteKey, onSuccess]);

  // Enhanced error handler with detailed logging
  const handleError = (error?: Error) => {
    console.error('[CAPTCHA ERROR] Turnstile verification failed:', error);
    console.error('[CAPTCHA ERROR] Current domain:', window.location.hostname);
    console.error('[CAPTCHA ERROR] Site key configured:', siteKey ? 'Yes' : 'No');
    console.error('[CAPTCHA ERROR] This usually means:');
    console.error('  1. The domain is not whitelisted in Cloudflare Turnstile dashboard');
    console.error('  2. The site key is invalid');
    console.error('  3. Network connectivity issues');

    // Call the original error handler if provided
    if (onError) {
      onError();
    }

    // Bypass CAPTCHA on error to allow signup (with warning)
    console.warn('[CAPTCHA BYPASS] Allowing signup despite CAPTCHA error (development safety)');
    onSuccess('dev-bypass-token');
  };

  const handleExpire = () => {
    console.warn('[CAPTCHA] Token expired, user needs to retry');
    if (onExpire) {
      onExpire();
    }
  };

  const handleSuccess = (token: string) => {
    console.log('[CAPTCHA] Verification successful');
    onSuccess(token);
  };

  if (!siteKey) {
    return (
      <div className="text-xs text-muted-foreground text-center py-2">
        ⚠️ CAPTCHA not configured (development mode)
      </div>
    );
  }

  return (
    <div>
      <TurnstileWidget
        siteKey={siteKey}
        onSuccess={handleSuccess}
        onError={handleError}
        onExpire={handleExpire}
        options={{
          theme: 'light',
          size: 'normal',
        }}
      />
      <div className="text-xs text-muted-foreground text-center mt-2">
        Current domain: {window.location.hostname}
      </div>
    </div>
  );
};
